import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// ── Dark teal design tokens (match Dashboard / AdminDashboard) ──
const T = {
  bg:          "#060d1f",
  bgCard:      "rgba(10, 25, 50, 0.82)",
  teal:        "#00c9b1",
  tealDim:     "rgba(0, 201, 177, 0.18)",
  tealBorder:  "rgba(0, 201, 177, 0.28)",
  text:        "#e0f7f4",
  textDim:     "rgba(160, 210, 220, 0.65)",
  header:      "linear-gradient(135deg, #0a2540 0%, #0d3060 100%)",
  font:        "'Poppins', 'Segoe UI', sans-serif",
};

export default function Profile() {
  const navigate = useNavigate();
  const cardRef = useRef();

  const [userData, setUserData] = useState(null);

  // ✅ ROBUST LOAD: try localStorage first, then fall back to Firestore
  //    so the profile works even after logout/reload/back navigation.
  useEffect(() => {
    // 1) Try localStorage instantly (no network)
    try {
      const cached = JSON.parse(localStorage.getItem("user"));
      if (cached && (cached.name || cached.email)) {
        setUserData(cached);
        return;
      }
    } catch (err) {
      console.warn("Profile: localStorage parse error", err);
    }

    // 2) Otherwise wait for auth + fetch fresh from Firestore
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        // No logged-in user — bounce to home/login
        navigate("/");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", firebaseUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          setUserData(data);
          localStorage.setItem("user", JSON.stringify(data)); // refill cache
        } else {
          // Could be a vendor — also check vendors collection
          const vSnap = await getDoc(doc(db, "vendors", firebaseUser.uid));
          if (vSnap.exists()) {
            const v = vSnap.data();
            setUserData({
              id:    v.id || "N/A",
              name:  v.name,
              email: v.email,
              phone: v.phone,
              subdistrict: v.subdistrict,
              photoURL: v.photoURL || v.imageUrl || null
            });
          } else {
            setUserData({
              name:  firebaseUser.displayName || "User",
              email: firebaseUser.email || "N/A",
              phone: "N/A"
            });
          }
        }
      } catch (err) {
        console.error("Profile: Firestore fetch failed", err);
        // Show whatever we know from the auth object so we don't stay stuck
        setUserData({
          name:  firebaseUser.displayName || "User",
          email: firebaseUser.email || "N/A",
          phone: "N/A"
        });
      }
    });

    return () => unsub();
  }, [navigate]);

  // ✅ DOWNLOAD CARD (PNG snapshot)
  const downloadCard = async () => {
    try {
      if (!cardRef.current) return;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2
      });
      const link = document.createElement("a");
      link.download = "My_ID_Card.png";
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  // ✅ LOADING STATE
  if (!userData) {
    return (
      <div style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: T.bg,
        color: T.text,
        fontFamily: T.font
      }}>
        <h2>Loading Profile...</h2>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "radial-gradient(ellipse at top, #0a1628 0%, #060d1f 70%)",
        padding: 20,
        fontFamily: T.font
      }}
    >
      <div style={{ width: "100%", maxWidth: 340 }}>
        {/* ── CARD ─────────────────────────────────────── */}
        <div
          ref={cardRef}
          style={{
            padding: "28px 22px",
            borderRadius: 24,
            background: T.bgCard,
            border: `1px solid ${T.tealBorder}`,
            boxShadow:
              "0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(0, 201, 177, 0.08)",
            textAlign: "center",
            color: T.text,
            backdropFilter: "blur(12px)",
          }}
        >
          <h2
            style={{
              margin: "0 0 4px 0",
              fontSize: 24,
              fontWeight: 800,
              background: "linear-gradient(135deg, #00c9b1, #0077cc)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: 0.5
            }}
          >
            Connect
          </h2>
          <p style={{ color: T.textDim, fontSize: 13, margin: "4px 0 14px" }}>
            User Profile
          </p>

          {/* PROFILE IMAGE — falls back to gradient initial avatar */}
          <Avatar
            src={userData?.photoURL}
            label={userData?.name || userData?.email || "U"}
            size={92}
          />

          {/* USER DETAILS */}
          <div style={{ textAlign: "left", margin: "10px 6px 14px" }}>
            <DetailRow label="ID"    value={userData?.id    || "N/A"} />
            <DetailRow label="Name"  value={userData?.name  || "User"} />
            <DetailRow label="Email" value={userData?.email || "N/A"} />
            <DetailRow label="Phone" value={userData?.phone || "N/A"} />
            {userData?.subdistrict && (
              <DetailRow label="Sub District" value={userData.subdistrict} />
            )}
          </div>

          {/* QR */}
          <div
            style={{
              display: "inline-block",
              background: "#fff",
              padding: 8,
              borderRadius: 10,
              marginTop: 6,
            }}
          >
            <QRCode
              value={userData?.id || userData?.uid || "USER"}
              size={90}
            />
          </div>

          {/* VERIFIED BADGE */}
          <div
            style={{
              marginTop: 16,
              padding: "8px 14px",
              background: "linear-gradient(135deg, #00c9b1, #0077cc)",
              color: "#fff",
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 0.3,
              display: "inline-block",
              boxShadow: "0 4px 16px rgba(0,201,177,0.35)"
            }}
          >
            ✔ Verified User
          </div>
        </div>

        {/* ── BUTTONS ─────────────────────────────────── */}
        <div
          style={{
            marginTop: 18,
            display: "flex",
            gap: 10,
            justifyContent: "center",
          }}
        >
          <button
            onClick={downloadCard}
            style={btnPrimary}
          >
            ⬇ Download
          </button>

          <button
            onClick={() => navigate("/dashboard")}
            style={btnGhost}
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Avatar that always renders something — never a broken image ─
function Avatar({ src, label, size = 92 }) {
  const [broken, setBroken] = useState(!src);
  const initial = (label || "U").trim().charAt(0).toUpperCase();
  if (broken || !src) {
    return (
      <div style={{
        width: size, height: size, borderRadius: "50%",
        background: "linear-gradient(135deg, #00c9b1, #0077cc)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 800,
        fontSize: Math.round(size * 0.42),
        border: `3px solid #00c9b1`,
        boxShadow: "0 0 18px rgba(0, 201, 177, 0.45)",
        marginBottom: 14,
      }}>{initial}</div>
    );
  }
  return (
    <img
      src={src}
      alt="profile"
      onError={() => setBroken(true)}
      style={{
        width: size, height: size, borderRadius: "50%",
        objectFit: "cover", border: `3px solid #00c9b1`,
        boxShadow: "0 0 18px rgba(0, 201, 177, 0.45)",
        marginBottom: 14,
      }}
    />
  );
}

// ── Small helper row component ──────────────────────
function DetailRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "6px 0",
        borderBottom: "1px solid rgba(0, 201, 177, 0.1)",
        fontSize: 13,
      }}
    >
      <span style={{ color: "rgba(160, 210, 220, 0.75)", fontWeight: 600 }}>
        {label}
      </span>
      <span
        style={{
          color: "#e0f7f4",
          maxWidth: "60%",
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ── Button styles ──────────────────────────────────────
const btnPrimary = {
  padding: "11px 22px",
  borderRadius: 25,
  border: "none",
  background: "linear-gradient(135deg, #00c9b1, #0077cc)",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
  boxShadow: "0 6px 20px rgba(0, 201, 177, 0.35)",
  fontFamily: "'Poppins', 'Segoe UI', sans-serif",
};

const btnGhost = {
  padding: "11px 22px",
  borderRadius: 25,
  border: "1px solid rgba(0, 201, 177, 0.28)",
  background: "rgba(0, 201, 177, 0.08)",
  color: "#00c9b1",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "'Poppins', 'Segoe UI', sans-serif",
};